import { Clock, Check, CheckCheck, XCircle, AlertTriangle, Eye, MousePointerClick } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type MessageStatusType = 'pending' | 'sent' | 'delayed' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed';

interface MessageStatusProps {
  status: MessageStatusType;
  className?: string;
  openedCount?: number;
  clickedCount?: number;
  bounceReason?: string;
}

const statusConfig: Record<MessageStatusType, { 
  icon: typeof Clock; 
  label: string; 
  className: string;
}> = {
  pending: {
    icon: Clock,
    label: 'Sending...',
    className: 'text-muted-foreground animate-pulse',
  },
  sent: {
    icon: Check,
    label: 'Sent',
    className: 'text-muted-foreground',
  },
  delayed: {
    icon: AlertTriangle,
    label: 'Delivery delayed',
    className: 'text-warning',
  },
  delivered: {
    icon: CheckCheck,
    label: 'Delivered',
    className: 'text-success',
  },
  opened: {
    icon: Eye,
    label: 'Opened',
    className: 'text-info',
  },
  clicked: {
    icon: MousePointerClick,
    label: 'Link clicked',
    className: 'text-primary',
  },
  bounced: {
    icon: XCircle,
    label: 'Bounced',
    className: 'text-destructive',
  },
  failed: {
    icon: XCircle,
    label: 'Failed to send',
    className: 'text-destructive',
  },
};

export function MessageStatus({ status, className, openedCount, clickedCount, bounceReason }: MessageStatusProps) {
  const config = statusConfig[status];
  if (!config) return null;

  const Icon = config.icon;

  // Build detailed tooltip text
  let tooltipText = config.label;
  if (status === 'opened' && openedCount && openedCount > 1) {
    tooltipText = `Opened ${openedCount} times`;
  } else if (status === 'clicked' && clickedCount) {
    tooltipText = `Clicked ${clickedCount} link${clickedCount > 1 ? 's' : ''}`;
  } else if (status === 'bounced' && bounceReason) {
    tooltipText = `Bounced: ${bounceReason}`;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('inline-flex', className)}>
            <Icon className={cn('h-3.5 w-3.5', config.className)} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
